import React, { useState, useRef, useEffect, useCallback } from 'react';
import {
  Target, Calendar, Bell, UserPlus, Check, ChevronRight,
  ChevronLeft, Activity, AlertCircle, Send, Users
} from 'lucide-react';
import { EARTH, TYPOGRAPHY } from '../theme/constants.js';

function AccountabilityWidget({
  isOpen,
  onToggle,
  projectName,
  sections = [],
  onUpdateGoal,
  accountability = {},
  onAccountabilityChange,
  onMarkSectionComplete
}) {
  // Goal & Deadline State — driven by lifted props
  const [deadline, setDeadline] = useState(accountability.deadline || '');
  const [goalSections, setGoalSections] = useState(accountability.goalSections || 0);
  const [isEditingGoal, setIsEditingGoal] = useState(false);

  // Nudge Settings State
  const [nudgesEnabled, setNudgesEnabled] = useState(accountability.nudgesEnabled || false);
  const [nudgeFrequency, setNudgeFrequency] = useState(accountability.nudgeFrequency || 'daily');
  const [nudgeEmail, setNudgeEmail] = useState(accountability.nudgeEmail || '');
  const [nudgeSaved, setNudgeSaved] = useState(false);

  // Accountability Partner State
  const [partnerEmail, setPartnerEmail] = useState(accountability.partnerEmail || '');
  const [partnerName, setPartnerName] = useState(accountability.partnerName || '');
  const [partnerInvited, setPartnerInvited] = useState(accountability.partnerInvited || false);
  const [shareProgress, setShareProgress] = useState(accountability.shareProgress !== false);

  // Streak & Stats State
  const [currentStreak, setCurrentStreak] = useState(0);
  const [longestStreak, setLongestStreak] = useState(0);
  const [totalSessionsThisWeek, setTotalSessionsThisWeek] = useState(0);
  const [lastActiveDate, setLastActiveDate] = useState(null);

  // Session tracking state
  const sessionStartRef = useRef(Date.now());
  const [sessionSectionsCompleted, setSessionSectionsCompleted] = useState(0);
  const [sessionDuration, setSessionDuration] = useState(0);
  const prevCompletedRef = useRef(0);

  // Tab State
  const [activeTab, setActiveTab] = useState('goals');

  // ── SESSION TRACKING ──────────────────────────────────────
  const SESSION_HISTORY_KEY = 'arrangementlab_sessions';

  // Load session history from localStorage
  const getSessionHistory = () => {
    try {
      const stored = localStorage.getItem(SESSION_HISTORY_KEY);
      return stored ? JSON.parse(stored) : [];
    } catch { return []; }
  };

  // Save a session entry
  const saveSessionEntry = useCallback((durationMs, sectionsCompleted) => {
    const history = getSessionHistory();
    const entry = {
      date: new Date().toISOString(),
      dateKey: new Date().toISOString().split('T')[0],
      duration: Math.round(durationMs / 1000 / 60), // minutes
      sectionsCompleted,
      projectName: projectName || 'Untitled'
    };
    // Merge with existing entry for today if exists
    const todayKey = entry.dateKey;
    const todayIdx = history.findIndex(h => h.dateKey === todayKey && h.projectName === entry.projectName);
    if (todayIdx >= 0) {
      history[todayIdx].duration += entry.duration;
      history[todayIdx].sectionsCompleted += entry.sectionsCompleted;
    } else {
      history.push(entry);
    }
    // Keep last 90 days
    const cutoff = new Date();
    cutoff.setDate(cutoff.getDate() - 90);
    const filtered = history.filter(h => new Date(h.date) > cutoff);
    try {
      localStorage.setItem(SESSION_HISTORY_KEY, JSON.stringify(filtered));
    } catch (err) {
      // Best-effort session tracking — a quota failure on unload must not throw.
      console.warn('Session history save failed:', err);
    }
    return filtered;
  }, [projectName]);

  // Track elapsed time every 30 seconds
  useEffect(() => {
    const interval = setInterval(() => {
      setSessionDuration(Math.round((Date.now() - sessionStartRef.current) / 1000 / 60));
    }, 30000);
    return () => clearInterval(interval);
  }, []);

  // Track section completions by watching the sections prop
  useEffect(() => {
    const currentCompleted = sections.filter(s => s.status === 'done').length;
    if (prevCompletedRef.current > 0 && currentCompleted > prevCompletedRef.current) {
      const newlyCompleted = currentCompleted - prevCompletedRef.current;
      setSessionSectionsCompleted(prev => prev + newlyCompleted);
    }
    prevCompletedRef.current = currentCompleted;
  }, [sections]);

  // Save session on page unload
  useEffect(() => {
    const handleUnload = () => {
      const elapsed = Date.now() - sessionStartRef.current;
      if (elapsed > 60000) { // Only save if session was > 1 minute
        saveSessionEntry(elapsed, sessionSectionsCompleted);
      }
    };
    const handleVisibility = () => {
      if (document.visibilityState === 'hidden') handleUnload();
    };
    window.addEventListener('beforeunload', handleUnload);
    document.addEventListener('visibilitychange', handleVisibility);
    return () => {
      window.removeEventListener('beforeunload', handleUnload);
      // Previously leaked: the visibilitychange listener was added with an
      // anonymous fn and never removed, accumulating one per re-render.
      document.removeEventListener('visibilitychange', handleVisibility);
    };
  }, [sessionSectionsCompleted, saveSessionEntry]);

  // ── ARRANGEMENT VELOCITY ──────────────────────────────────
  const getVelocityData = () => {
    const history = getSessionHistory();
    if (history.length === 0) return { weeklyRate: 0, projectedCompletion: null, weeklyData: [] };

    // Group by week
    const weekMap = {};
    history.forEach(h => {
      const d = new Date(h.date);
      const weekStart = new Date(d);
      weekStart.setDate(d.getDate() - d.getDay());
      const weekKey = weekStart.toISOString().split('T')[0];
      if (!weekMap[weekKey]) weekMap[weekKey] = { sections: 0, minutes: 0, weekKey };
      weekMap[weekKey].sections += h.sectionsCompleted;
      weekMap[weekKey].minutes += h.duration;
    });

    const weeks = Object.values(weekMap).sort((a, b) => a.weekKey.localeCompare(b.weekKey));
    const recentWeeks = weeks.slice(-4);
    const avgSectionsPerWeek = recentWeeks.length > 0
      ? recentWeeks.reduce((sum, w) => sum + w.sections, 0) / recentWeeks.length
      : 0;

    // Project completion
    const remaining = sections.filter(s => s.status !== 'done').length;
    const weeksToComplete = avgSectionsPerWeek > 0 ? Math.ceil(remaining / avgSectionsPerWeek) : null;
    let projectedDate = null;
    if (weeksToComplete !== null) {
      projectedDate = new Date();
      projectedDate.setDate(projectedDate.getDate() + (weeksToComplete * 7));
    }

    return {
      weeklyRate: Math.round(avgSectionsPerWeek * 10) / 10,
      projectedCompletion: projectedDate,
      weeklyData: weeks.slice(-8), // Last 8 weeks for chart
      totalMinutes: history.reduce((sum, h) => sum + h.duration, 0),
      totalSessions: history.length
    };
  };

  // ── WEEKLY ACTIVITY (REAL DATA) ───────────────────────────
  const getWeeklyActivity = () => {
    const history = getSessionHistory();
    const today = new Date();
    const dayOfWeek = today.getDay(); // 0=Sun
    const activeDays = new Set();

    history.forEach(h => {
      const d = new Date(h.date);
      const diff = Math.floor((today - d) / (1000 * 60 * 60 * 24));
      if (diff < 7) {
        activeDays.add(d.toISOString().split('T')[0]);
      }
    });

    // Also mark today as active (current session)
    activeDays.add(today.toISOString().split('T')[0]);

    return activeDays;
  };

  // Sync accountability changes back to parent for persistence
  useEffect(() => {
    if (onAccountabilityChange) {
      onAccountabilityChange({ deadline, goalSections, nudgesEnabled, nudgeFrequency, nudgeEmail, partnerEmail, partnerName, partnerInvited, shareProgress });
    }
  }, [deadline, goalSections, nudgesEnabled, nudgeFrequency, nudgeEmail, partnerEmail, partnerName, partnerInvited, shareProgress]);

  // Calculate progress
  const completedSections = sections.filter(s => s.status === 'done').length;
  const inProgressSections = sections.filter(s => s.status === 'in-progress').length;
  const totalSections = sections.length;
  const progressPercent = totalSections > 0 ? Math.round((completedSections / totalSections) * 100) : 0;

  // Calculate days until deadline
  const getDaysUntilDeadline = () => {
    if (!deadline) return null;
    const today = new Date();
    const deadlineDate = new Date(deadline);
    const diffTime = deadlineDate - today;
    const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return diffDays;
  };

  const daysLeft = getDaysUntilDeadline();

  // Find next incomplete section for "Mark section complete"
  const getNextIncompleteSection = () => {
    const inProgress = sections.find(s => s.status === 'in-progress');
    if (inProgress) return inProgress;
    const notStarted = sections.find(s => s.status === 'not-started');
    return notStarted || null;
  };

  const nextSection = getNextIncompleteSection();

  // Handle marking the next section complete
  const handleMarkComplete = () => {
    if (nextSection && onMarkSectionComplete) {
      onMarkSectionComplete(nextSection.id);
    }
  };

  // Get thoughtful prompt based on progress — now context-aware
  const getThoughtfulPrompt = () => {
    if (progressPercent === 100) return "This one's done. What's next?";
    // Check for stalled sections
    const velocity = getVelocityData();
    if (nextSection && nextSection.status === 'in-progress') {
      return `Your ${nextSection.name} is in progress — what would finish it?`;
    }
    if (progressPercent >= 75) return "You're close. What's the one thing that would finish this?";
    if (progressPercent >= 50) return "Solid progress. Which section needs your attention today?";
    if (velocity.weeklyRate > 0 && progressPercent >= 25) {
      return `At ${velocity.weeklyRate} sections/week, you've got momentum. Keep going.`;
    }
    if (progressPercent >= 25) return "You've got momentum. What can you complete in the next hour?";
    if (daysLeft !== null && daysLeft <= 3 && daysLeft > 0) return "Deadline's close. What's the most important thing to tackle?";
    if (currentStreak >= 7) return "A week of consistency. That's how songs get finished.";
    if (currentStreak >= 3) return "Three days in a row. Keep showing up.";
    if (totalSections === 0) return "What's the first section of this track?";
    return "What can you get done today?";
  };

  // Streak update on mount
  useEffect(() => {
    let streakData = null;
    const stored = localStorage.getItem('arrangementlab_streak');
    if (stored) {
      try {
        streakData = JSON.parse(stored);
        setCurrentStreak(streakData.currentStreak || 0);
        setLongestStreak(streakData.longestStreak || 0);
        setLastActiveDate(streakData.lastActiveDate);
      } catch (err) {
        console.warn('Invalid streak data, resetting');
        localStorage.removeItem('arrangementlab_streak');
      }
    }
    const today = new Date().toDateString();
    const lastActive = streakData ? streakData.lastActiveDate : null;
    if (lastActive !== today) {
      const yesterday = new Date();
      yesterday.setDate(yesterday.getDate() - 1);
      const isConsecutive = lastActive === yesterday.toDateString();
      const newStreak = isConsecutive ? (streakData ? streakData.currentStreak + 1 : 1) : 1;
      const newLongest = Math.max(newStreak, streakData ? streakData.longestStreak : 0);
      setCurrentStreak(newStreak);
      setLongestStreak(newLongest);
      setLastActiveDate(today);
      localStorage.setItem('arrangementlab_streak', JSON.stringify({
        currentStreak: newStreak,
        longestStreak: newLongest,
        lastActiveDate: today
      }));
    }
  }, []);

  // Handle partner invite (simulated)
  const handleInvitePartner = () => {
    if (partnerEmail && partnerEmail.includes('@')) {
      setPartnerInvited(true);
    }
  };

  // Handle saving reminder settings to localStorage
  const handleSaveNudges = () => {
    localStorage.setItem('arrangementlab_nudges', JSON.stringify({
      enabled: nudgesEnabled,
      frequency: nudgeFrequency,
      email: nudgeEmail,
      savedAt: new Date().toISOString()
    }));
    setNudgeSaved(true);
    setTimeout(() => setNudgeSaved(false), 2000);
  };

  // Load saved nudge settings on mount
  useEffect(() => {
    const saved = localStorage.getItem('arrangementlab_nudges');
    if (saved) {
      try {
        const data = JSON.parse(saved);
        if (data.enabled !== undefined) setNudgesEnabled(data.enabled);
        if (data.frequency) setNudgeFrequency(data.frequency);
        if (data.email) setNudgeEmail(data.email);
      } catch {}
    }
  }, []);

  // Handle saving goal
  const handleSaveGoal = () => {
    setIsEditingGoal(false);
    if (onUpdateGoal) {
      onUpdateGoal({ deadline, goalSections });
    }
    localStorage.setItem('arrangementlab_goal_' + projectName, JSON.stringify({
      deadline,
      goalSections,
      createdAt: new Date().toISOString()
    }));
  };

  // Load saved goal on mount
  useEffect(() => {
    if (projectName) {
      const saved = localStorage.getItem('arrangementlab_goal_' + projectName);
      if (saved) {
        const data = JSON.parse(saved);
        setDeadline(data.deadline || '');
        setGoalSections(data.goalSections || 0);
      }
    }
  }, [projectName]);

  // Collapsed state - just show toggle button
  if (!isOpen) return null;

  return (
    <div className="fixed right-0 top-0 h-full w-80 shadow-xl z-20 flex flex-col" style={{ backgroundColor: '#fff', borderLeft: '1px solid ' + EARTH.stone[200] }}>
      {/* Header */}
      <div className="flex items-center justify-between p-4 border-b" style={{ borderColor: EARTH.stone[200], backgroundColor: EARTH.terracotta[50] }}>
        <div className="flex items-center gap-2">
          <Target className="w-5 h-5" style={{ color: EARTH.terracotta[600] }} />
          <h3 className="font-semibold" style={{ color: EARTH.terracotta[800] }}>Focus</h3>
          {currentStreak > 1 && (
            <span className="text-xs px-2 py-0.5 rounded-full" style={{ backgroundColor: EARTH.stone[100], color: EARTH.stone[600] }}>
              {currentStreak} days
            </span>
          )}
        </div>
        <button onClick={onToggle} className="p-1 rounded" style={{ color: EARTH.stone[500] }}>
          <ChevronRight className="w-5 h-5" />
        </button>
      </div>

      {/* Motivational Banner */}
      <div className="px-4 py-3" style={{ backgroundColor: EARTH.sage[50], borderBottom: '1px solid ' + EARTH.sage[200] }}>
        <p className="text-sm" style={{ color: EARTH.stone[600], fontStyle: 'italic' }}>{getThoughtfulPrompt()}</p>
      </div>

      {/* Tabs */}
      <div className="flex border-b" style={{ borderColor: EARTH.stone[200] }}>
        {[
          { id: 'goals', label: 'Goals', icon: Target },
          { id: 'nudges', label: 'Reminders', icon: Bell },
          { id: 'partner', label: 'Partner', icon: Users },
          { id: 'stats', label: 'Progress', icon: Activity }
        ].map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className="flex-1 py-2 px-1 text-xs font-medium flex flex-col items-center gap-1 transition-colors"
            style={{
              backgroundColor: activeTab === tab.id ? EARTH.terracotta[50] : 'transparent',
              color: activeTab === tab.id ? EARTH.terracotta[700] : EARTH.stone[500],
              borderBottom: activeTab === tab.id ? '2px solid ' + EARTH.terracotta[500] : '2px solid transparent'
            }}
          >
            <tab.icon className="w-4 h-4" />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Tab Content */}
      <div className="flex-1 overflow-y-auto p-4">

        {/* GOALS TAB */}
        {activeTab === 'goals' && (
          <div className="space-y-4">
            {/* Progress Overview */}
            <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.stone[50], border: '1px solid ' + EARTH.stone[200] }}>
              <div className="flex justify-between items-center mb-2">
                <span className="text-sm font-medium" style={{ color: EARTH.stone[700] }}>Overall Progress</span>
                <span className="text-lg font-bold" style={{ color: EARTH.terracotta[600] }}>{progressPercent}%</span>
              </div>
              <div className="w-full h-3 rounded-full overflow-hidden" style={{ backgroundColor: EARTH.stone[200] }}>
                <div
                  className="h-full rounded-full transition-all duration-500"
                  style={{
                    width: `${progressPercent}%`,
                    backgroundColor: progressPercent === 100 ? EARTH.sage[500] : EARTH.terracotta[500]
                  }}
                />
              </div>
              <div className="flex justify-between mt-2 text-xs" style={{ color: EARTH.stone[500] }}>
                <span>{completedSections} done</span>
                <span>{inProgressSections} in progress</span>
                <span>{totalSections - completedSections - inProgressSections} to do</span>
              </div>
            </div>

            {/* Deadline Section */}
            <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.sand[50], border: '1px solid ' + EARTH.sand[200] }}>
              <div className="flex justify-between items-center mb-3">
                <div className="flex items-center gap-2">
                  <Calendar className="w-4 h-4" style={{ color: EARTH.sand[600] }} />
                  <span className="text-sm font-medium" style={{ color: EARTH.sand[800] }}>Deadline</span>
                </div>
                {!isEditingGoal && (
                  <button onClick={() => setIsEditingGoal(true)} className="text-xs px-2 py-1 rounded" style={{ backgroundColor: EARTH.sand[200], color: EARTH.sand[700] }}>
                    {deadline ? 'Edit' : 'Set Goal'}
                  </button>
                )}
              </div>

              {isEditingGoal ? (
                <div className="space-y-3">
                  <div>
                    <label className="text-xs block mb-1" style={{ color: EARTH.stone[500] }}>Target Date</label>
                    <input
                      type="date"
                      value={deadline}
                      onChange={(e) => setDeadline(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg"
                      style={{ border: '1px solid ' + EARTH.stone[300] }}
                      min={new Date().toISOString().split('T')[0]}
                    />
                  </div>
                  <div className="flex gap-2">
                    <button onClick={handleSaveGoal} className="flex-1 py-2 rounded-lg text-sm font-medium" style={{ backgroundColor: EARTH.sage[500], color: '#fff' }}>
                      Save Goal
                    </button>
                    <button onClick={() => setIsEditingGoal(false)} className="px-4 py-2 rounded-lg text-sm" style={{ backgroundColor: EARTH.stone[200], color: EARTH.stone[600] }}>
                      Cancel
                    </button>
                  </div>
                </div>
              ) : deadline ? (
                <div>
                  <div className="text-2xl font-bold mb-1" style={{ color: daysLeft <= 3 ? EARTH.terracotta[600] : EARTH.sage[600] }}>
                    {daysLeft > 0 ? `${daysLeft} days left` : daysLeft === 0 ? "Due today!" : `${Math.abs(daysLeft)} days overdue`}
                  </div>
                  <div className="text-sm" style={{ color: EARTH.stone[500] }}>
                    Target: {new Date(deadline).toLocaleDateString('en-US', { weekday: 'long', month: 'short', day: 'numeric' })}
                  </div>
                  {daysLeft !== null && daysLeft <= 3 && daysLeft > 0 && progressPercent < 75 && (
                    <div className="mt-2 p-2 rounded-lg flex items-center gap-2" style={{ backgroundColor: EARTH.terracotta[100] }}>
                      <AlertCircle className="w-4 h-4" style={{ color: EARTH.terracotta[600] }} />
                      <span className="text-xs" style={{ color: EARTH.terracotta[700] }}>Deadline approaching! Pick up the pace.</span>
                    </div>
                  )}
                </div>
              ) : (
                <p className="text-sm" style={{ color: EARTH.stone[400] }}>No deadline set. Set a goal to stay on track!</p>
              )}
            </div>

            {/* Current Session */}
            {sessionDuration > 0 && (
              <div className="p-3 rounded-xl flex items-center justify-between" style={{ backgroundColor: EARTH.sage[50], border: '1px solid ' + EARTH.sage[200] }}>
                <div className="flex items-center gap-2">
                  <div className="w-2 h-2 rounded-full animate-pulse" style={{ backgroundColor: EARTH.sage[500] }} />
                  <span className="text-xs font-medium" style={{ color: EARTH.sage[700] }}>Session active</span>
                </div>
                <div className="text-right">
                  <span className="text-sm font-semibold" style={{ color: EARTH.sage[700] }}>{sessionDuration} min</span>
                  {sessionSectionsCompleted > 0 && (
                    <span className="text-xs ml-2" style={{ color: EARTH.sage[600] }}>{sessionSectionsCompleted} completed</span>
                  )}
                </div>
              </div>
            )}

            {/* Quick Actions */}
            <div className="space-y-2">
              <p className="text-xs font-medium" style={{ color: EARTH.stone[500] }}>QUICK ACTIONS</p>
              <button
                onClick={handleMarkComplete}
                disabled={!nextSection}
                className="w-full p-3 rounded-xl text-left flex items-center gap-3 transition-colors hover:bg-stone-100"
                style={{ backgroundColor: EARTH.stone[50], border: '1px solid ' + EARTH.stone[200], opacity: nextSection ? 1 : 0.5, cursor: nextSection ? 'pointer' : 'default' }}
              >
                <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: EARTH.sage[100] }}>
                  <Check className="w-4 h-4" style={{ color: EARTH.sage[600] }} />
                </div>
                <div>
                  <p className="text-sm font-medium" style={{ color: EARTH.stone[700] }}>
                    {nextSection ? `Complete "${nextSection.name}"` : 'All sections done!'}
                  </p>
                  <p className="text-xs" style={{ color: EARTH.stone[400] }}>
                    {nextSection ? (nextSection.status === 'in-progress' ? 'Currently in progress' : 'Next up') : 'Great work on this arrangement'}
                  </p>
                </div>
              </button>
            </div>
          </div>
        )}

        {/* NUDGES TAB */}
        {activeTab === 'nudges' && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.olive[50], border: '1px solid ' + EARTH.olive[200] }}>
              <div className="flex items-center justify-between mb-3">
                <div className="flex items-center gap-2">
                  <Bell className="w-4 h-4" style={{ color: EARTH.olive[600] }} />
                  <span className="text-sm font-medium" style={{ color: EARTH.olive[800] }}>Smart Reminders</span>
                </div>
                <button
                  onClick={() => setNudgesEnabled(!nudgesEnabled)}
                  className="w-12 h-6 rounded-full transition-colors relative"
                  style={{ backgroundColor: nudgesEnabled ? EARTH.sage[500] : EARTH.stone[300] }}
                >
                  <div
                    className="w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all shadow"
                    style={{ left: nudgesEnabled ? '26px' : '2px' }}
                  />
                </button>
              </div>

              {nudgesEnabled && (
                <div className="space-y-3 mt-4">
                  <div>
                    <label className="text-xs block mb-1" style={{ color: EARTH.stone[500] }}>Reminder Frequency</label>
                    <select
                      value={nudgeFrequency}
                      onChange={(e) => setNudgeFrequency(e.target.value)}
                      className="w-full px-3 py-2 text-sm rounded-lg"
                      style={{ border: '1px solid ' + EARTH.stone[300] }}
                    >
                      <option value="daily">Daily</option>
                      <option value="every-other-day">Every other day</option>
                      <option value="weekly">Weekly</option>
                    </select>
                  </div>
                  <div>
                    <label className="text-xs block mb-1" style={{ color: EARTH.stone[500] }}>Email for reminders</label>
                    <input
                      type="email"
                      value={nudgeEmail}
                      onChange={(e) => setNudgeEmail(e.target.value)}
                      placeholder="your@email.com"
                      className="w-full px-3 py-2 text-sm rounded-lg"
                      style={{ border: '1px solid ' + EARTH.stone[300] }}
                    />
                  </div>
                  <button onClick={handleSaveNudges} className="w-full py-2 rounded-lg text-sm font-medium transition-colors" style={{ backgroundColor: nudgeSaved ? EARTH.sage[500] : EARTH.olive[500], color: '#fff' }}>
                    {nudgeSaved ? '✓ Settings Saved' : 'Save Reminder Settings'}
                  </button>
                </div>
              )}
            </div>

            <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.stone[50], border: '1px solid ' + EARTH.stone[200] }}>
              <p className="text-sm font-medium mb-2" style={{ color: EARTH.stone[700] }}>What you'll receive:</p>
              <ul className="space-y-2 text-sm" style={{ color: EARTH.stone[600] }}>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5" style={{ color: EARTH.sage[500] }} />
                  <span>Progress updates toward your deadline</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5" style={{ color: EARTH.sage[500] }} />
                  <span>Gentle nudges when you haven't worked on a project</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 mt-0.5" style={{ color: EARTH.sage[500] }} />
                  <span>Celebration when you hit milestones</span>
                </li>
              </ul>
            </div>
          </div>
        )}

        {/* PARTNER TAB */}
        {activeTab === 'partner' && (
          <div className="space-y-4">
            <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.forest[50], border: '1px solid ' + EARTH.forest[200] }}>
              <div className="flex items-center gap-2 mb-3">
                <UserPlus className="w-4 h-4" style={{ color: EARTH.forest[600] }} />
                <span className="text-sm font-medium" style={{ color: EARTH.forest[800] }}>Accountability Partner</span>
              </div>

              {partnerInvited ? (
                <div className="space-y-3">
                  <div className="flex items-center gap-3 p-3 rounded-lg" style={{ backgroundColor: EARTH.forest[100] }}>
                    <div className="w-10 h-10 rounded-full flex items-center justify-center text-white font-medium" style={{ backgroundColor: EARTH.forest[500] }}>
                      {partnerName ? partnerName.charAt(0).toUpperCase() : partnerEmail.charAt(0).toUpperCase()}
                    </div>
                    <div>
                      <p className="text-sm font-medium" style={{ color: EARTH.forest[800] }}>{partnerName || 'Partner'}</p>
                      <p className="text-xs" style={{ color: EARTH.forest[600] }}>{partnerEmail}</p>
                    </div>
                    <Check className="w-5 h-5 ml-auto" style={{ color: EARTH.sage[500] }} />
                  </div>
                  <div className="flex items-center justify-between">
                    <span className="text-sm" style={{ color: EARTH.stone[600] }}>Share progress updates</span>
                    <button
                      onClick={() => setShareProgress(!shareProgress)}
                      className="w-12 h-6 rounded-full transition-colors relative"
                      style={{ backgroundColor: shareProgress ? EARTH.sage[500] : EARTH.stone[300] }}
                    >
                      <div
                        className="w-5 h-5 rounded-full bg-white absolute top-0.5 transition-all shadow"
                        style={{ left: shareProgress ? '26px' : '2px' }}
                      />
                    </button>
                  </div>
                  <button
                    onClick={() => { setPartnerInvited(false); setPartnerEmail(''); setPartnerName(''); }}
                    className="w-full py-2 rounded-lg text-sm"
                    style={{ backgroundColor: EARTH.stone[200], color: EARTH.stone[600] }}
                  >
                    Remove Partner
                  </button>
                </div>
              ) : (
                <div className="space-y-3">
                  <p className="text-sm" style={{ color: EARTH.stone[600] }}>
                    Invite someone to keep you accountable. They'll receive weekly progress updates.
                  </p>
                  <div>
                    <label className="text-xs block mb-1" style={{ color: EARTH.stone[500] }}>Partner's name</label>
                    <input
                      type="text"
                      value={partnerName}
                      onChange={(e) => setPartnerName(e.target.value)}
                      placeholder="Their name"
                      className="w-full px-3 py-2 text-sm rounded-lg"
                      style={{ border: '1px solid ' + EARTH.stone[300] }}
                    />
                  </div>
                  <div>
                    <label className="text-xs block mb-1" style={{ color: EARTH.stone[500] }}>Partner's email</label>
                    <input
                      type="email"
                      value={partnerEmail}
                      onChange={(e) => setPartnerEmail(e.target.value)}
                      placeholder="partner@email.com"
                      className="w-full px-3 py-2 text-sm rounded-lg"
                      style={{ border: '1px solid ' + EARTH.stone[300] }}
                    />
                  </div>
                  <button
                    onClick={handleInvitePartner}
                    className="w-full py-2 rounded-lg text-sm font-medium flex items-center justify-center gap-2"
                    style={{ backgroundColor: EARTH.forest[500], color: '#fff' }}
                  >
                    <Send className="w-4 h-4" />
                    Send Invite
                  </button>
                </div>
              )}
            </div>

            <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.stone[50], border: '1px solid ' + EARTH.stone[200] }}>
              <p className="text-sm" style={{ color: EARTH.stone[600], lineHeight: 1.6 }}>
                Having someone who knows your deadline makes it real. They'll get a simple weekly update showing your progress—nothing more.
              </p>
            </div>
          </div>
        )}

        {/* PROGRESS TAB */}
        {activeTab === 'stats' && (
          <div className="space-y-4">
            {/* Progress Summary */}
            <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.stone[50], border: '1px solid ' + EARTH.stone[200] }}>
              <span className="text-xs font-medium" style={{ color: EARTH.stone[500] }}>THIS PROJECT</span>
              <div className="mt-3 grid grid-cols-2 gap-4">
                <div>
                  <div className="text-2xl font-semibold" style={{ color: EARTH.stone[800] }}>{completedSections}/{totalSections}</div>
                  <div className="text-xs" style={{ color: EARTH.stone[500] }}>sections complete</div>
                </div>
                <div>
                  <div className="text-2xl font-semibold" style={{ color: EARTH.stone[800] }}>{progressPercent}%</div>
                  <div className="text-xs" style={{ color: EARTH.stone[500] }}>overall progress</div>
                </div>
              </div>
            </div>

            {/* Arrangement Velocity */}
            {(() => {
              const velocity = getVelocityData();
              return (
                <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.terracotta[50], border: '1px solid ' + EARTH.terracotta[200] }}>
                  <span className="text-xs font-medium" style={{ color: EARTH.terracotta[600] }}>ARRANGEMENT VELOCITY</span>
                  <div className="mt-3 grid grid-cols-2 gap-4">
                    <div>
                      <div className="text-2xl font-semibold" style={{ color: EARTH.terracotta[700] }}>
                        {velocity.weeklyRate > 0 ? velocity.weeklyRate : '—'}
                      </div>
                      <div className="text-xs" style={{ color: EARTH.stone[500] }}>sections/week</div>
                    </div>
                    <div>
                      <div className="text-2xl font-semibold" style={{ color: EARTH.terracotta[700] }}>
                        {velocity.totalMinutes > 0 ? (velocity.totalMinutes < 60 ? `${velocity.totalMinutes}m` : `${Math.round(velocity.totalMinutes / 60 * 10) / 10}h`) : '—'}
                      </div>
                      <div className="text-xs" style={{ color: EARTH.stone[500] }}>total time tracked</div>
                    </div>
                  </div>

                  {/* Velocity bar chart — last 8 weeks */}
                  {velocity.weeklyData.length > 1 && (
                    <div className="mt-4">
                      <div className="flex items-end gap-1 h-16">
                        {velocity.weeklyData.map((week, i) => {
                          const maxSections = Math.max(...velocity.weeklyData.map(w => w.sections), 1);
                          const height = week.sections > 0 ? Math.max((week.sections / maxSections) * 100, 8) : 4;
                          const isLatest = i === velocity.weeklyData.length - 1;
                          return (
                            <div key={i} className="flex-1 flex flex-col items-center gap-1">
                              <div
                                className="w-full rounded-t"
                                style={{
                                  height: `${height}%`,
                                  backgroundColor: isLatest ? EARTH.terracotta[500] : EARTH.terracotta[300],
                                  minHeight: '2px',
                                  transition: 'height 0.3s ease'
                                }}
                                title={`${week.sections} sections`}
                              />
                            </div>
                          );
                        })}
                      </div>
                      <div className="flex justify-between mt-1">
                        <span className="text-[10px]" style={{ color: EARTH.stone[400] }}>
                          {velocity.weeklyData.length}w ago
                        </span>
                        <span className="text-[10px]" style={{ color: EARTH.stone[400] }}>this week</span>
                      </div>
                    </div>
                  )}

                  {/* Projected completion */}
                  {velocity.projectedCompletion && progressPercent < 100 && (
                    <div className="mt-3 p-2 rounded-lg" style={{ backgroundColor: EARTH.terracotta[100] }}>
                      <p className="text-xs" style={{ color: EARTH.terracotta[700] }}>
                        At this pace, you'll finish by{' '}
                        <strong>{velocity.projectedCompletion.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}</strong>
                        {deadline && (() => {
                          const deadlineDate = new Date(deadline);
                          const diff = Math.ceil((deadlineDate - velocity.projectedCompletion) / (1000 * 60 * 60 * 24));
                          if (diff >= 0) return <span style={{ color: EARTH.sage[600] }}> — {diff} days ahead of deadline</span>;
                          return <span style={{ color: EARTH.terracotta[600] }}> — {Math.abs(diff)} days past deadline</span>;
                        })()}
                      </p>
                    </div>
                  )}

                  {velocity.weeklyRate === 0 && (
                    <p className="text-xs mt-2" style={{ color: EARTH.stone[400] }}>
                      Complete sections to start tracking your velocity.
                    </p>
                  )}
                </div>
              );
            })()}

            {/* Consistency */}
            <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.sage[50], border: '1px solid ' + EARTH.sage[200] }}>
              <span className="text-xs font-medium" style={{ color: EARTH.sage[600] }}>CONSISTENCY</span>
              <div className="mt-3 flex items-baseline gap-2">
                <span className="text-2xl font-semibold" style={{ color: EARTH.sage[700] }}>{currentStreak}</span>
                <span className="text-sm" style={{ color: EARTH.sage[600] }}>{currentStreak === 1 ? 'day' : 'days'} in a row</span>
              </div>
              {longestStreak > currentStreak && (
                <div className="text-xs mt-1" style={{ color: EARTH.stone[500] }}>Best: {longestStreak} days</div>
              )}
            </div>

            {/* Milestones */}
            <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.stone[50], border: '1px solid ' + EARTH.stone[200] }}>
              <span className="text-xs font-medium" style={{ color: EARTH.stone[500] }}>MILESTONES</span>
              <div className="mt-3 space-y-2">
                {[
                  { label: 'Set a deadline', done: deadline !== '' },
                  { label: 'Complete first section', done: completedSections >= 1 },
                  { label: 'Reach halfway', done: progressPercent >= 50 },
                  { label: 'Three days consistent', done: longestStreak >= 3 },
                  { label: 'Finish the project', done: progressPercent === 100 },
                ].map((milestone, i) => (
                  <div key={i} className="flex items-center gap-2">
                    <div
                      className="w-4 h-4 rounded-full flex items-center justify-center"
                      style={{
                        backgroundColor: milestone.done ? EARTH.sage[500] : EARTH.stone[200],
                        border: milestone.done ? 'none' : '1px solid ' + EARTH.stone[300]
                      }}
                    >
                      {milestone.done && <Check className="w-2.5 h-2.5 text-white" />}
                    </div>
                    <span
                      className="text-sm"
                      style={{
                        color: milestone.done ? EARTH.stone[700] : EARTH.stone[400],
                        textDecoration: milestone.done ? 'line-through' : 'none'
                      }}
                    >
                      {milestone.label}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            {/* Weekly Activity — REAL DATA */}
            <div className="p-4 rounded-xl" style={{ backgroundColor: EARTH.olive[50], border: '1px solid ' + EARTH.olive[200] }}>
              <div className="flex items-center gap-2 mb-3">
                <Activity className="w-4 h-4" style={{ color: EARTH.olive[600] }} />
                <span className="text-sm font-medium" style={{ color: EARTH.olive[700] }}>This Week</span>
              </div>
              <div className="flex justify-between">
                {['M', 'T', 'W', 'T', 'F', 'S', 'S'].map((day, i) => {
                  const today = new Date();
                  const todayDay = today.getDay();
                  const dayIndex = i === 6 ? 0 : i + 1;
                  const isToday = dayIndex === todayDay;
                  // Calculate the actual date for this day of the week
                  const dayDate = new Date(today);
                  const diff = dayIndex - todayDay;
                  dayDate.setDate(today.getDate() + diff);
                  const dateKey = dayDate.toISOString().split('T')[0];
                  const activeDays = getWeeklyActivity();
                  const isActive = activeDays.has(dateKey);
                  const isFuture = dayDate > today && !isToday;
                  return (
                    <div key={i} className="flex flex-col items-center gap-1">
                      <span className="text-xs" style={{ color: EARTH.stone[500] }}>{day}</span>
                      <div
                        className="w-6 h-6 rounded-full flex items-center justify-center"
                        style={{
                          backgroundColor: isActive ? EARTH.sage[500] : isFuture ? EARTH.stone[100] : EARTH.stone[200],
                          border: isToday ? '2px solid ' + EARTH.terracotta[500] : 'none'
                        }}
                      >
                        {isActive && <Check className="w-3 h-3 text-white" />}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export { AccountabilityWidget };
